import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../shared/api/client';
import { queryKeys } from '../../shared/api/query-keys';

/* ── Types (espelham backend support.dto.ts / support.controller.ts + ticket.entity.ts) ── */

export type TicketSeverity = 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

/** Valor real persistido: WAITING_CUSTOMER (membro do enum chama-se WAITING_CLIENT). */
export type TicketStatus = 'OPEN' | 'IN_PROGRESS' | 'WAITING_CUSTOMER' | 'RESOLVED' | 'CLOSED';

export type TicketAuthorType = 'USER' | 'AGENT' | 'SYSTEM';

export interface TicketMessage {
  id: string;
  ticketId: string;
  content: string;
  authorType: TicketAuthorType;
  createdById: string | null;
  createdAt: string;
  attachments: Record<string, unknown> | null;
}

export interface SupportTicket {
  id: string;
  tenantId: string;
  companyId: string | null;
  number: string;
  subject: string;
  description: string | null;
  severity: TicketSeverity;
  status: TicketStatus;
  assignedTo: string | null;
  createdBy: string | null;
  slaLimitAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  csatScore: number | null;
  messages: TicketMessage[];
  createdAt: string;
  updatedAt: string;
}

/** GET /support/tickets → { data, total, page, limit }. */
export interface SupportTicketsListResponse {
  data: SupportTicket[];
  total: number;
  page: number;
  limit: number;
}

/** POST /support/tickets — createdBy vem da sessão. */
export interface CreateTicketInput {
  companyId?: string;
  subject: string;
  description?: string;
  severity: TicketSeverity;
  assignedTo?: string;
}

/** POST :id/respond — body { content, authorType, attachments? }. */
export interface RespondTicketInput {
  content: string;
  authorType: TicketAuthorType;
  attachments?: Record<string, unknown>;
}

/** POST :id/close — body { csatScore? }. */
export interface CloseTicketInput {
  csatScore?: number;
}

export interface SupportTicketsParams {
  status?: string;
  severity?: string;
  page?: number;
  limit?: number;
}

const BASE_PATH = '/support/tickets';

/* ── Queries ── */

export function useSupportTickets(tenantId: string, params?: SupportTicketsParams) {
  const status = params?.status;
  const severity = params?.severity;
  const page = params?.page ?? 1;
  const limit = params?.limit ?? 20;
  return useQuery({
    queryKey: [
      ...queryKeys.support.tickets(tenantId),
      { status: status ?? 'ALL', severity: severity ?? 'ALL', page, limit },
    ],
    queryFn: () =>
      apiClient.get<SupportTicketsListResponse>(BASE_PATH, {
        params: {
          page: String(page),
          limit: String(limit),
          ...(status && status !== 'ALL' ? { status } : {}),
          ...(severity && severity !== 'ALL' ? { severity } : {}),
        },
      }),
    enabled: !!tenantId,
  });
}

export function useSupportTicket(tenantId: string, id: string) {
  return useQuery({
    queryKey: [...queryKeys.support.tickets(tenantId), id],
    queryFn: () => apiClient.get<SupportTicket>(`${BASE_PATH}/${id}`),
    enabled: !!tenantId && !!id,
  });
}

/* ── Mutations (confirmadas em support.controller.ts) ── */

function useInvalidateSupportTickets(tenantId: string) {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: queryKeys.support.tickets(tenantId) });
  };
}

export function useCreateTicket(tenantId: string) {
  const invalidate = useInvalidateSupportTickets(tenantId);
  return useMutation({
    mutationFn: (input: CreateTicketInput) => apiClient.post<SupportTicket>(BASE_PATH, input),
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useRespondTicket(tenantId: string) {
  const invalidate = useInvalidateSupportTickets(tenantId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: RespondTicketInput }) =>
      apiClient.post<SupportTicket>(`${BASE_PATH}/${id}/respond`, input),
    onSuccess: () => {
      invalidate();
    },
  });
}

export function useCloseTicket(tenantId: string) {
  const invalidate = useInvalidateSupportTickets(tenantId);
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: CloseTicketInput }) =>
      apiClient.post<SupportTicket>(`${BASE_PATH}/${id}/close`, input),
    onSuccess: () => {
      invalidate();
    },
  });
}

/** POST :id/csat — body { score } (1–5). */
export function useCsatTicket(tenantId: string) {
  const invalidate = useInvalidateSupportTickets(tenantId);
  return useMutation({
    mutationFn: ({ id, score }: { id: string; score: number }) =>
      apiClient.post<SupportTicket>(`${BASE_PATH}/${id}/csat`, { score }),
    onSuccess: () => {
      invalidate();
    },
  });
}
